import { useState } from 'react'
import { DreamitySubCategoryResDto } from '@/store/types/category.types'
import { DreamityPlanResDto } from '@/store/types/plan.types'
import { useLanguage } from '@/hooks/useLanguage'
import { useCategory } from '@/hooks/useCategory'
import { useToastContext } from '@/store/contexts/ToastContext'
import { useConfirmContext } from '@/store/contexts/ConfirmContext'
import { t } from '@/utils/i18n'
import PlanCard from '../PlanCard/PlanCard'
import Button from '@/components/common/Button/Button'
import { IconChevronDown, IconChevronRight, IconEdit, IconTrash, IconPlus } from '@/components/icons/Icons'
import Modal from '@/components/common/Modal/Modal'
import { Input } from '@/components/common/Input/Input'
import { formatCurrency } from '@/utils/currency'
import './CategoryAccordion.css'

interface CategoryAccordionProps {
  category: DreamitySubCategoryResDto
  plans: DreamityPlanResDto[]
  planProgressRates: Record<string, number>
  onPlanClick?: (planId: string) => void
  onPlanEdit?: (plan: DreamityPlanResDto) => void
  onPlanDelete?: (planId: string) => void
  onPlanToggleComplete?: (plan: DreamityPlanResDto) => void
  onPlanCreate?: () => void
  onCategoryEdit?: (category: DreamitySubCategoryResDto) => void
  onCategoryDelete?: (categoryId: number) => void
}

function CategoryAccordion({
  category,
  plans,
  planProgressRates,
  onPlanClick,
  onPlanEdit,
  onPlanDelete,
  onPlanToggleComplete,
  onPlanCreate,
  onCategoryEdit,
  onCategoryDelete,
}: CategoryAccordionProps) {
  const { showToast } = useToastContext()
  const { confirm } = useConfirmContext()
  const { language } = useLanguage()
  const { updateSubCategory } = useCategory()
  const [isOpen, setIsOpen] = useState(true)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editCategoryName, setEditCategoryName] = useState(category.subCategoryName)

  const categoryPlans = plans.filter(() => true)

  const handleCategoryEdit = () => {
    setIsEditModalOpen(true)
  }

  const handleCategoryDelete = async () => {
    const confirmed = await confirm({ 
      message: t('category.deleteConfirm', language),
      variant: 'danger'
    })
    if (confirmed) {
      onCategoryDelete?.(category.id)
    }
  }

  return (
    <div className="category-accordion">
      <div className="category-accordion__header" onClick={() => setIsOpen(!isOpen)}>
        <div className="category-accordion__title">
          <span className="category-accordion__icon">{isOpen ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}</span>
          <h3>{category.subCategoryName}</h3>
          <span className="category-accordion__count">({categoryPlans.length})</span>
          <span className="category-accordion__total-budget">
            <span className="category-accordion__total-budget-label">{t('dreams.itemBudget', language)}</span>
            <span className="category-accordion__total-budget-value">{formatCurrency(categoryPlans.reduce((sum, p) => sum + (p.totalItemPrice || 0), 0), language)}</span>
          </span>
        </div>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {onCategoryEdit && (
            <Button variant="ghost" size="sm" onClick={handleCategoryEdit} title={t('common.edit', language)} aria-label={t('common.edit', language)} className="!min-w-9 !h-9 !p-1.5 !rounded-lg !border !border-[var(--color-card-border)] !bg-[var(--color-card)] !text-[var(--color-text-primary)] inline-flex items-center justify-center hover:!bg-slate-100"><IconEdit size={18} /></Button>
          )}
          {onCategoryDelete && (
            <Button variant="danger" size="sm" onClick={handleCategoryDelete} title={t('common.delete', language)} aria-label={t('common.delete', language)} className="!min-w-9 !h-9 !p-1.5 !rounded-lg !border !border-red-200 !bg-[var(--color-card)] !text-red-600 inline-flex items-center justify-center hover:!bg-red-50"><IconTrash size={18} /></Button>
          )}
          {onPlanCreate && (
            <Button variant="primary" size="sm" onClick={onPlanCreate} title={language === 'ko' ? '계획 생성' : 'Create Plan'} aria-label={language === 'ko' ? '계획 생성' : 'Create Plan'} className="!min-w-9 !h-9 !p-1.5 !rounded-lg !border !border-[var(--color-primary)] !bg-[var(--color-card)] !text-[var(--color-primary)] inline-flex items-center justify-center hover:!bg-[var(--color-primary)] hover:!text-white"><IconPlus size={18} /></Button>
          )}
        </div>
      </div>
      {isOpen && (
        <div className="category-accordion__content">
          {categoryPlans.length === 0 ? (
            <p className="category-accordion__empty">
              {t('category.noPlans', language)}
            </p>
          ) : (
            <div className="category-accordion__plans">
              {categoryPlans.map((plan) => (
                <PlanCard
                  key={plan.planId}
                  plan={plan}
                  progressRate={planProgressRates[plan.planId]}
                  onClick={() => onPlanClick?.(plan.planId)}
                  onEdit={onPlanEdit}
                  onDelete={onPlanDelete}
                onToggleComplete={onPlanToggleComplete}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* 카테고리 수정 모달 */}
      {onCategoryEdit && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title={t('category.editTitle', language)}
          size="sm"
        >
          <div className="category-accordion__modal-content">
            <Input
              label={t('category.name', language)}
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
            />
            <div className="category-accordion__modal-actions">
              <Button
                onClick={async () => {
                  try {
                    await updateSubCategory(category.id, {
                      mainCategoryId: category.mainCategoryId,
                      subCategoryCode: category.subCategoryCode,
                      subCategoryNameKo: language === 'ko' ? editCategoryName : category.subCategoryName,
                      subCategoryNameEn: language === 'en' ? editCategoryName : category.subCategoryName,
                    })
                    setIsEditModalOpen(false)
                    onCategoryEdit?.(category)
                  } catch (error) {
                    showToast(t('messages.updateFailed', language), 'error')
                  }
                }}
              >
                {t('common.save', language)}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default CategoryAccordion

